import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Send,
  Loader2,
  Sparkles,
  FileText,
  X,
  Settings,
  Mic,
  Keyboard,
  Trash2,
  Volume2,
  VolumeX,
  FolderOpen,
  Upload,
  Gift,
  ChevronLeft,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check,
} from 'lucide-react';
import { supabase } from '../../../utils/supabaseStorage';
import {
  agentChatStream,
  agentOnboard,
  agentRenew,
  agentTranscribe,
  agentTtsStream,
  agentClearMessages,
  agentDeleteInstance,
  agentListFiles,
  agentDeleteFile,
  agentUploadFile,
  agentReinstateAfterBan,
  blobToBase64,
  type AgentChatContentPart,
  type AgentInstanceFileRow,
} from '../../../utils/agentApi';
import { extractPdfText, extractDocxText } from '../../../utils/extractDocumentText';
import {
  getSpeechRecognitionConstructor,
  shouldUseWebSpeechStt,
  mergeSpeechFinalChunks,
  mergeVoiceTranscriptCandidates,
  requestMicrophoneStream,
  type BrowserSpeechRecognition,
  type SpeechRecognitionErrorEvent,
  type SpeechRecognitionResultEvent,
} from '../../../utils/webSpeechRecognition';
import { useAppStore } from '../../../store/useAppStore';
import type {
  BillingPeriod,
  UserAgentInstance,
  PublicAgentChatProps,
} from '../../../types/agent';
import { VoiceCaptureModal } from '../VoiceCaptureModal';
import { ModalContainer } from '../../ModalContainer';
import { FormFieldRenderer } from '../../admin/preview/FormFieldRenderer';
import {
  flattenOnboardingFields,
  getOnboardingAnswerKey,
  normalizeOnboardingSections,
} from '../../../utils/onboardingFormSchema';
import { extractNextSpeakableSegment, stripMarkdownForSpeech } from '../../../utils/speechText';
import { playOpenAiSpeechSsePcm, type PcmPlaybackReuse } from '../../../utils/openaiSpeechSsePlayback';
import {
  buildOnboardingAnswersForApi,
  readFileAsText,
  readFileAsDataURL,
  isImageFile,
  extractAgentApiError,
  parseStoredUserMessage,
  debugAgentLog,
  publicAgentDisplayTitle,
  publicAgentDisplayDescription,
  onboardingFieldValue,
  validateOnboardingRequired,
  creditsForPeriod,
  periodEnabled,
  BILLING_PERIOD_UI_ORDER,
  billingPeriodLabel,
  billingPeriodBlurb,
  billingCreditBadgeText,
  billingPeriodAccessAdjective,
  isAgentPlanExpired,
  SCROLL_BOTTOM_THRESHOLD_PX,
  DEFAULT_STT_LANG,
  type ChatMessage,
  type UserMessageAttachment,
} from './publicAgentHelpers';
import { AssistantMessageBubble, PublicAgentCoverMedia, UserMessageBubble } from './AgentMessageViews';
import { MultilingualTagline } from './PublicAgentOnboardingTrustCards';
import { applyAgentStreamMeta } from './agentStreamMeta';
import { AgentPlanExpiredModal } from './AgentPlanExpiredModal';
import { effectiveSttLocale } from '../../../utils/agentVoiceConfig';

export function PublicAgentChatRoot({ pageConfig, template, session, embedInPanel = false, onClosePanel }: PublicAgentChatProps) {
  const layer = embedInPanel ? 'absolute' : 'fixed';
  /** When false, hide mic/Tap to talk, Whisper path, and assistant TTS (template flag). */
  const voiceFeatureEnabled = template.voice_enabled === true;
  const { openInsufficientCreditsModalWithAmount, openModal } = useAppStore();
  const [instance, setInstance] = useState<UserAgentInstance | null>(null);
  const [loadingInstance, setLoadingInstance] = useState(true);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('month');
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  /** Pending images for the next send (vision turns). */
  const [pendingImageAttachments, setPendingImageAttachments] = useState<{ url: string; filename: string }[]>([]);
  /** Filenames for docs/text inserted into the composer from the file library (same send). */
  const [pendingDocumentFilenames, setPendingDocumentFilenames] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  /** SSE tool phases (Tavily / Serper / LLM rounds) for the in-flight assistant message. */
  const [streamActivity, setStreamActivity] = useState<string | null>(null);
  const [headerVisible, setHeaderVisible] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);
  /** Ignore scroll events caused by scrollIntoView — they fight user “scroll up to hide header”. */
  const suppressHeaderFromProgrammaticScrollRef = useRef(false);
  /** Only auto-scroll on new tokens when user is near the bottom (or just sent a message). */
  const stickToBottomRef = useRef(true);
  /** Batches many token updates into one smooth follow during SSE. */
  const scrollBottomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Scroll the in-flight assistant row to the top of the viewport so the reply is read from the start. */
  const lastStreamingAssistantRef = useRef<HTMLDivElement | null>(null);
  const streamTopAlignedForLengthRef = useRef<number | null>(null);
  const [fileLibraryOpen, setFileLibraryOpen] = useState(false);
  /** Shown when Edge upload fails or user must onboard first. */
  const [fileAttachError, setFileAttachError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [instanceFiles, setInstanceFiles] = useState<AgentInstanceFileRow[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [clearingOrDeleting, setClearingOrDeleting] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [renewingPlan, setRenewingPlan] = useState(false);

  /** Must match initial `voiceMode` or early TTS / voice checks read the wrong mode. */
  const voiceModeRef = useRef(voiceFeatureEnabled);
  const [voiceMode, setVoiceMode] = useState(voiceFeatureEnabled);
  useEffect(() => {
    voiceModeRef.current = voiceMode;
  }, [voiceMode]);

  useEffect(() => {
    if (!voiceFeatureEnabled) {
      voiceModeRef.current = false;
      setVoiceMode(false);
    }
  }, [voiceFeatureEnabled]);

  const voiceTtsStorageKey = `hyper-public-agent-voice-tts-${template.id}`;
  const [voiceTtsEnabled, setVoiceTtsEnabled] = useState(() => {
    try {
      const raw = localStorage.getItem(voiceTtsStorageKey);
      if (raw === '0') return false;
      if (raw === '1') return true;
    } catch {
      /* private mode / quota */
    }
    return true;
  });
  const voiceTtsEnabledRef = useRef(voiceTtsEnabled);
  useEffect(() => {
    voiceTtsEnabledRef.current = voiceTtsEnabled;
  }, [voiceTtsEnabled]);
  useEffect(() => {
    try {
      localStorage.setItem(voiceTtsStorageKey, voiceTtsEnabled ? '1' : '0');
    } catch {
      /* */
    }
  }, [voiceTtsStorageKey, voiceTtsEnabled]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(voiceTtsStorageKey);
      if (raw === '0') setVoiceTtsEnabled(false);
      else if (raw === '1') setVoiceTtsEnabled(true);
      else setVoiceTtsEnabled(true);
    } catch {
      setVoiceTtsEnabled(true);
    }
  }, [voiceTtsStorageKey]);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTranscribing, setRecordingTranscribing] = useState(false);
  const [playingTts, setPlayingTts] = useState(false);
  /** Autoplay blocked — user taps Play (banner or bubble). */
  const [ttsPlaybackPending, setTtsPlaybackPending] = useState<{ index: number; url: string } | null>(null);
  /** Which assistant bubble is fetching speech audio (OpenAI streaming TTS). */
  const [ttsLoadingIndex, setTtsLoadingIndex] = useState<number | null>(null);
  /** Which assistant bubble is playing speech audio. */
  const [ttsPlayingIndex, setTtsPlayingIndex] = useState<number | null>(null);
  /** Current assistant TTS playback (pause when user turns Speak off). */
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  /** Abort in-flight OpenAI speech stream (PCM SSE). */
  const ttsStreamAbortRef = useRef<AbortController | null>(null);
  /** Active Web Audio context for streaming TTS (close on stop). */
  const ttsAudioContextRef = useRef<AudioContext | null>(null);
  /** One AudioContext + timeline across multi-segment assistant TTS (gapless chaining). */
  const ttsPcmCtxRef = useRef<AudioContext | null>(null);
  const ttsPcmNextTimeRef = useRef({ current: 0 });
  /** Shown when OpenAI TTS fails (e.g. invalid OPENAI_API_KEY); auto-clears. */
  const [ttsErrorHint, setTtsErrorHint] = useState<string | null>(null);
  const [lastVoiceTranscript, setLastVoiceTranscript] = useState('');
  /** Web Speech: true while there is interim (non-final) text — used to disable Stop & send until user pauses. */
  const [voiceSpeechInterim, setVoiceSpeechInterim] = useState(false);
  /** Mirrors `lastVoiceTranscript` for send-on-stop (onend can run before last onresult updates refs). */
  const lastVoiceTranscriptRef = useRef('');
  /** Set true when user cancels / aborts so pending Web Speech "stop & send" does not fire. */
  const voiceStopSendCancelledRef = useRef(false);
  /** Voice capture modal (mic viz + Stop & send / Cancel). */
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const voiceModalOpenRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaChunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const speechRecognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  /** Cumulative finalized transcript segments only (Web Speech). */
  const speechFinalsRef = useRef('');
  /** Full line: finals + current interim; used for display and send on user stop. */
  const voiceLineRef = useRef('');
  /** True only when user tapped "Tap to stop" — opens Cancel/Send review, not on engine auto-restart. */
  const userStoppedListeningRef = useRef(false);
  /** Text finalized before the current SpeechRecognition instance (browser often ends session after silence). */
  const spokenPrefixRef = useRef('');
  const skipSendOnEndRef = useRef(false);
  /** When true, MediaRecorder onstop discards audio (user cancelled from modal). */
  const voiceCancelRef = useRef(false);
  /** Mic stream for Web Speech — acquired before recognition.start() so embeds get a permission prompt. */
  const voiceSttMicStreamRef = useRef<MediaStream | null>(null);
  /** Snapshot from modal at "Stop & send" (before recognition.stop settles). */
  const voiceStopSnapshotRef = useRef('');
  /** True after Stop & send has run send/close (onend or fallback). */
  const voiceStopDeliveredRef = useRef(false);
  const instanceRef = useRef<UserAgentInstance | null>(null);
  const finalizeWebSpeechStopRef = useRef<() => void>(() => {});
  const sendMessageRef = useRef<(textOverride?: string) => Promise<void>>(async () => {});
  const startWebSpeechRef = useRef<(opts?: { resume?: boolean }) => void>(() => {});
  /** Coalesce streamed assistant deltas; microtask flush avoids waiting up to one frame (RAF) per chunk. */
  const streamDeltaBufferRef = useRef('');
  const streamFlushScheduledRef = useRef(false);
  /** Raw assistant reply text for incremental TTS (updated in stream `onDelta`, before UI flush). */
  const assistantStreamAccumRef = useRef('');
  /** Byte offset into `stripMarkdownForSpeech(assistantStreamAccumRef)` already queued for TTS. */
  const ttsPlainOffsetRef = useRef(0);
  /** Bumped on `stopTtsPlayback` and each send to cancel in-flight streaming TTS. */
  const ttsStreamGenRef = useRef(0);
  /** Sequential playback chain for streaming TTS segments. */
  const ttsPlayChainRef = useRef<Promise<void>>(Promise.resolve());

  const flushPendingStreamDeltas = useCallback(() => {
    streamFlushScheduledRef.current = false;
    const chunk = streamDeltaBufferRef.current;
    streamDeltaBufferRef.current = '';
    if (!chunk) return;
    setMessages((prev) => {
      const next = [...prev];
      const lastIdx = next.length - 1;
      if (lastIdx >= 0 && next[lastIdx].role === 'assistant') {
        next[lastIdx] = { role: 'assistant', content: next[lastIdx].content + chunk };
      }
      return next;
    });
  }, []);

  useEffect(() => {
    lastVoiceTranscriptRef.current = lastVoiceTranscript;
  }, [lastVoiceTranscript]);

  useEffect(() => {
    instanceRef.current = instance;
  }, [instance]);

  useEffect(() => {
    voiceModalOpenRef.current = voiceModalOpen;
  }, [voiceModalOpen]);

  const webSpeechSupported = useMemo(() => shouldUseWebSpeechStt(), []);

  const onboardingFormEnabled = template.onboarding_form_enabled !== false;
  const onboardingSections = useMemo(
    () =>
      normalizeOnboardingSections(template.onboarding_form_schema, {
        onboardingFormEnabled: onboardingFormEnabled,
      }),
    [template.onboarding_form_schema, onboardingFormEnabled]
  );
  const activeOnboardingFields = useMemo(
    () =>
      flattenOnboardingFields(onboardingSections).filter((f) => !f.hidden && f.type !== 'prompt_builder'),
    [onboardingSections]
  );
  /** Sections that have at least one visible field (wizard steps). */
  const visibleOnboardingSections = useMemo(
    () => onboardingSections.filter((section) => section.fields.some((f) => !f.hidden)),
    [onboardingSections]
  );
  const pricingOnly = !onboardingFormEnabled;
  const sectionCount = visibleOnboardingSections.length;
  const billingStepIndex = pricingOnly ? 0 : 1 + sectionCount;
  const onboardTotalSteps = pricingOnly ? 1 : sectionCount + 2;
  const [onboardWizardStep, setOnboardWizardStep] = useState(0);
  const pricing = template.pricing || {};
  const displayTitle = publicAgentDisplayTitle(template, pageConfig);
  const displayDescription = publicAgentDisplayDescription(template, pageConfig);

  const loadInstance = useCallback(async () => {
    setLoadingInstance(true);
    const { data: row } = await supabase
      .from('user_agent_instances')
      .select('*')
      .eq('agent_template_id', template.id)
      .eq('hy_user_id', session.hy_user_id)
      .maybeSingle();

    if (row) {
      setInstance(row as UserAgentInstance);
      const { data: msgRows } = await supabase
        .from('agent_messages')
        .select('role, content')
        .eq('instance_id', row.id)
        .order('created_at', { ascending: true })
        .limit(200);
      if (msgRows?.length) {
        stickToBottomRef.current = true;
        setMessages(
          msgRows.map((m) => {
            if (m.role === 'user') {
              const { body, attachments } = parseStoredUserMessage(String(m.content ?? ''));
              return {
                role: 'user' as const,
                content: body,
                ...(attachments.length ? { attachments } : {}),
              };
            }
            return { role: 'assistant' as const, content: m.content };
          })
        );
      }
    }
    setLoadingInstance(false);
  }, [template.id, session.hy_user_id]);

  useEffect(() => {
    loadInstance();
  }, [loadInstance]);

  const isBannedInstance = instance?.status === 'banned';

  const planExpired = useMemo(
    () => !!(instance && isAgentPlanExpired(instance)),
    [instance]
  );
  const showPlanExpiredModal = planExpired && !settingsOpen && !isBannedInstance;

  const handleRenewPlan = useCallback(async () => {
    if (!instance || !planExpired) return;
    const appId = pageConfig.app_id?.trim() || 'web';
    setRenewingPlan(true);
    try {
      const { ok, status, data } = await agentRenew({ appId, instanceId: instance.id });
      const payload = data as {
        success?: boolean;
        instance?: UserAgentInstance;
        error?: string;
        code?: string;
      };
      if (ok && payload?.success && payload.instance) {
        setInstance(payload.instance);
        return;
      }
      if (status === 402 || payload?.code === 'insufficient_credits') {
        openInsufficientCreditsModalWithAmount(instance.credits_per_period || 10);
        return;
      }
      alert(payload?.error || 'Could not renew');
    } finally {
      setRenewingPlan(false);
    }
  }, [instance, planExpired, pageConfig.app_id, openInsufficientCreditsModalWithAmount]);

  /**
   * While the assistant reply streams, align the top of that bubble to the top of the scroll
   * area (once per turn) so the user sees the beginning of the answer. Avoid bottom-anchoring
   * during SSE — that hid the start of long replies above the fold.
   */
  useLayoutEffect(() => {
    if (!sending) {
      streamTopAlignedForLengthRef.current = null;
      return;
    }
    const last = messages[messages.length - 1];
    if (last?.role !== 'assistant') return;
    const turnKey = messages.length;
    if (streamTopAlignedForLengthRef.current === turnKey) return;

    let attempts = 0;
    const maxAttempts = 40;
    const run = () => {
      const node = lastStreamingAssistantRef.current;
      if (node) {
        streamTopAlignedForLengthRef.current = turnKey;
        suppressHeaderFromProgrammaticScrollRef.current = true;
        node.scrollIntoView({ block: 'start', behavior: 'smooth' });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            suppressHeaderFromProgrammaticScrollRef.current = false;
            if (scrollRef.current) {
              lastScrollTopRef.current = scrollRef.current.scrollTop;
            }
          });
        });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        requestAnimationFrame(run);
      }
    };
    run();
  }, [sending, messages.length, messages[messages.length - 1]?.role]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    /** During an open assistant stream, top-align handles visibility; do not pull to the bottom. */
    if (sending) {
      return;
    }

    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const distFromBottom = maxScroll - el.scrollTop;
    const nearBottom = distFromBottom < SCROLL_BOTTOM_THRESHOLD_PX;
    if (!stickToBottomRef.current && !nearBottom) {
      lastScrollTopRef.current = el.scrollTop;
      return;
    }

    const runScroll = () => {
      suppressHeaderFromProgrammaticScrollRef.current = true;
      const end = messagesEndRef.current;
      if (end) {
        end.scrollIntoView({ block: 'end', behavior: 'smooth' });
      } else {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          suppressHeaderFromProgrammaticScrollRef.current = false;
          if (scrollRef.current) {
            lastScrollTopRef.current = scrollRef.current.scrollTop;
            stickToBottomRef.current = true;
          }
        });
      });
    };

    if (scrollBottomDebounceRef.current) {
      clearTimeout(scrollBottomDebounceRef.current);
      scrollBottomDebounceRef.current = null;
    }
    runScroll();

    return () => {
      if (scrollBottomDebounceRef.current) {
        clearTimeout(scrollBottomDebounceRef.current);
        scrollBottomDebounceRef.current = null;
      }
    };
  }, [messages, sending]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) lastScrollTopRef.current = el.scrollTop;
  }, [instance?.id, loadingInstance]);

  const applyScrollDirectionToHeader = useCallback((scrollTop: number, prevTop: number) => {
    if (scrollTop <= 1) {
      setHeaderVisible(true);
      return;
    }
    const maxScroll = scrollRef.current
      ? Math.max(0, scrollRef.current.scrollHeight - scrollRef.current.clientHeight)
      : 0;
    if (maxScroll > 2 && scrollTop >= maxScroll - 2) {
      setHeaderVisible(true);
      return;
    }
    const delta = scrollTop - prevTop;
    if (delta < -1.5) setHeaderVisible(false);
    else if (delta > 1.5) setHeaderVisible(true);
  }, []);

  const onConversationScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    const distFromBottom = maxScroll - el.scrollTop;
    stickToBottomRef.current = distFromBottom < SCROLL_BOTTOM_THRESHOLD_PX;

    if (suppressHeaderFromProgrammaticScrollRef.current) {
      lastScrollTopRef.current = el.scrollTop;
      return;
    }
    const st = el.scrollTop;
    const prev = lastScrollTopRef.current;
    lastScrollTopRef.current = st;
    applyScrollDirectionToHeader(st, prev);
  }, [applyScrollDirectionToHeader]);

  const onConversationWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (suppressHeaderFromProgrammaticScrollRef.current) return;
    // deltaY < 0: wheel up → content moves up → toward older messages → hide header
    // deltaY > 0: wheel down → toward newer messages → show header
    if (e.deltaY < -0.5) setHeaderVisible(false);
    else if (e.deltaY > 0.5) setHeaderVisible(true);
  }, []);

  const refreshInstanceFiles = useCallback(async () => {
    if (!instance || !template.file_uploads_enabled) {
      debugAgentLog(
        'PublicAgentChat.tsx:refreshInstanceFiles',
        'skipped',
        'C',
        { reason: !instance ? 'no_instance' : 'uploads_disabled' }
      );
      return;
    }
    const appId = pageConfig.app_id?.trim() || 'web';
    setLoadingFiles(true);
    try {
      const { ok, status, data } = await agentListFiles({ appId, instanceId: instance.id });
      debugAgentLog('PublicAgentChat.tsx:refreshInstanceFiles', 'list_files_result', 'C', {
        ok,
        status,
        filesLen: Array.isArray((data as { files?: unknown })?.files)
          ? (data as { files: unknown[] }).files.length
          : -1,
        err: !ok ? extractAgentApiError(data) : undefined,
      });
      if (ok && data && typeof data === 'object') {
        const raw = (data as { files?: unknown }).files;
        setInstanceFiles(Array.isArray(raw) ? (raw as AgentInstanceFileRow[]) : []);
      }
    } finally {
      setLoadingFiles(false);
    }
  }, [instance, pageConfig.app_id, template.file_uploads_enabled]);

  const uploadAttachedFileToServer = useCallback(
    async (file: File): Promise<{ ok: boolean; error?: string }> => {
      const appId = pageConfig.app_id?.trim() || 'web';
      debugAgentLog('PublicAgentChat.tsx:uploadAttachedFileToServer', 'preconditions', 'E', {
        hasAppId: !!appId,
        hasInstance: !!instance,
        fileNameLen: file.name?.length ?? 0,
      });
      if (!appId || !instance) {
        const err = 'Complete agent onboarding before uploading files.';
        setFileAttachError(err);
        return { ok: false, error: err };
      }
      if (isAgentPlanExpired(instance)) {
        const err = 'Your plan access has expired. Renew to upload files.';
        setFileAttachError(err);
        return { ok: false, error: err };
      }
      const { ok, status, data } = await agentUploadFile({ appId, instanceId: instance.id, file });
      debugAgentLog('PublicAgentChat.tsx:uploadAttachedFileToServer', 'upload_file_result', 'A', {
        ok,
        status,
        err: !ok ? extractAgentApiError(data) : undefined,
      });
      if (!ok) {
        const err = extractAgentApiError(data);
        setFileAttachError(err);
        return { ok: false, error: err };
      }
      void refreshInstanceFiles();
      return { ok: true };
    },
    [instance, pageConfig.app_id, refreshInstanceFiles]
  );

  const processLibraryFile = useCallback(
    async (file: File): Promise<boolean> => {
      const mime = file.type || '';
      const lower = file.name.toLowerCase();
      const kind = (() => {
        if (isImageFile(file)) return 'image';
        if (mime === 'text/plain' || lower.endsWith('.txt')) return 'txt';
        if (mime === 'application/pdf' || lower.endsWith('.pdf')) return 'pdf';
        if (
          mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          lower.endsWith('.docx')
        )
          return 'docx';
        return 'unsupported';
      })();
      debugAgentLog('PublicAgentChat.tsx:processLibraryFile', 'classified', 'B', {
        kind,
        mimeEmpty: !mime,
      });
      if (isImageFile(file)) {
        try {
          const url = await readFileAsDataURL(file);
          flushSync(() => {
            setPendingImageAttachments((prev) => [...prev, { url, filename: file.name }]);
          });
          const up = await uploadAttachedFileToServer(file);
          return up.ok;
        } catch {
          setFileAttachError('Could not read image.');
          return false;
        }
      }
      if (mime === 'text/plain' || lower.endsWith('.txt')) {
        try {
          const t = await readFileAsText(file);
          flushSync(() => {
            setInput((prev) =>
              prev ? `${prev}\n\n[File: ${file.name}]\n${t}` : `[File: ${file.name}]\n${t}`
            );
            setPendingDocumentFilenames((prev) => [...prev, file.name]);
          });
        } catch {
          setFileAttachError('Could not read text file.');
          return false;
        }
        const up = await uploadAttachedFileToServer(file);
        return up.ok;
      }
      if (mime === 'application/pdf' || lower.endsWith('.pdf')) {
        try {
          const text = await extractPdfText(file);
          flushSync(() => {
            setInput((prev) =>
              prev ? `${prev}\n\n[PDF: ${file.name}]\n${text}` : `[PDF: ${file.name}]\n${text}`
            );
            setPendingDocumentFilenames((prev) => [...prev, file.name]);
          });
        } catch {
          alert('Could not read PDF');
          return false;
        }
        const up = await uploadAttachedFileToServer(file);
        return up.ok;
      }
      if (
        mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        lower.endsWith('.docx')
      ) {
        try {
          const text = await extractDocxText(file);
          flushSync(() => {
            setInput((prev) =>
              prev ? `${prev}\n\n[DOCX: ${file.name}]\n${text}` : `[DOCX: ${file.name}]\n${text}`
            );
            setPendingDocumentFilenames((prev) => [...prev, file.name]);
          });
        } catch {
          alert('Could not read document');
          return false;
        }
        const up = await uploadAttachedFileToServer(file);
        return up.ok;
      }
      setFileAttachError(`Unsupported file type: ${file.name}`);
      return false;
    },
    [uploadAttachedFileToServer]
  );

  const onLibraryFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    /** `FileList` is live — snapshot before `value = ''` or length becomes 0 in many browsers. */
    const picked = e.target.files ? Array.from(e.target.files) : [];
    debugAgentLog('PublicAgentChat.tsx:onLibraryFilesChange', 'change_event', 'B', {
      initialCount: picked.length,
    });
    e.target.value = '';
    if (!picked.length) return;
    debugAgentLog('PublicAgentChat.tsx:onLibraryFilesChange', 'files_selected', 'B', {
      count: picked.length,
    });
    setFileAttachError(null);
    let allOk = true;
    try {
      for (const file of picked) {
        const ok = await processLibraryFile(file);
        if (!ok) allOk = false;
      }
      await refreshInstanceFiles();
      if (allOk) {
        setFileAttachError(null);
        setFileLibraryOpen(false);
      }
    } catch (err) {
      console.error('[PublicAgentChat] library file processing', err);
      debugAgentLog('PublicAgentChat.tsx:onLibraryFilesChange', 'catch', 'B', {
        err: err instanceof Error ? err.message : 'unknown',
      });
      setFileAttachError(err instanceof Error ? err.message : 'Could not process file');
    }
  };

  const openFileLibrary = useCallback(() => {
    if (instanceRef.current && isAgentPlanExpired(instanceRef.current)) return;
    setFileLibraryOpen(true);
    setFileAttachError(null);
    void refreshInstanceFiles();
  }, [refreshInstanceFiles]);

  useEffect(() => {
    if (settingsOpen && instance) void refreshInstanceFiles();
  }, [settingsOpen, instance, refreshInstanceFiles]);

  useEffect(() => {
    if (instance?.status === 'banned' && template.file_uploads_enabled) {
      void refreshInstanceFiles();
    }
  }, [instance?.id, instance?.status, template.file_uploads_enabled, refreshInstanceFiles]);

  useEffect(() => {
    if (!ttsErrorHint) return;
    const t = window.setTimeout(() => setTtsErrorHint(null), 14000);
    return () => clearTimeout(t);
  }, [ttsErrorHint]);

  const vadActive = isRecording || playingTts || recordingTranscribing;

  const stopTtsPlayback = useCallback(() => {
    ttsStreamGenRef.current += 1;
    ttsPlayChainRef.current = Promise.resolve();
    ttsStreamAbortRef.current?.abort();
    ttsStreamAbortRef.current = null;
    try {
      void ttsAudioContextRef.current?.close();
    } catch {
      /* */
    }
    ttsAudioContextRef.current = null;
    try {
      void ttsPcmCtxRef.current?.close();
    } catch {
      /* */
    }
    ttsPcmCtxRef.current = null;
    ttsPcmNextTimeRef.current.current = 0;
    const a = ttsAudioRef.current;
    if (a) {
      try {
        a.pause();
      } catch {
        /* */
      }
      ttsAudioRef.current = null;
    }
    setTtsPlaybackPending(null);
    setTtsLoadingIndex(null);
    setTtsPlayingIndex(null);
    setPlayingTts(false);
  }, []);

  const playTtsSegmentInternal = useCallback(
    async (
      textOrPrefetch: string | { prefetched: Promise<Response> },
      opts: {
        messageIndex?: number;
        allowWithoutVoiceMode?: boolean;
        gen?: number;
        streaming: boolean;
      }
    ) => {
      if (!voiceFeatureEnabled) return;
      if (opts.gen !== undefined && opts.gen !== ttsStreamGenRef.current) return;
      if (!opts.allowWithoutVoiceMode && !voiceModeRef.current) return;
      if (!voiceTtsEnabledRef.current) return;
      const messageIndex = opts.messageIndex;
      const appId = pageConfig.app_id?.trim() || 'web';
      const sig = ttsStreamAbortRef.current?.signal;
      if (!sig) return;

      if (typeof textOrPrefetch === 'string') {
        const plain = stripMarkdownForSpeech(textOrPrefetch);
        if (!plain) return;
      }

      if (typeof messageIndex === 'number') {
        setTtsLoadingIndex(messageIndex);
      }
      try {
        const resPromise =
          typeof textOrPrefetch === 'string'
            ? agentTtsStream({
                appId,
                text: stripMarkdownForSpeech(textOrPrefetch).slice(0, 4000),
                signal: sig,
                instanceId: instance?.id,
              })
            : textOrPrefetch.prefetched;

        const res = await resPromise;
        if (opts.gen !== undefined && opts.gen !== ttsStreamGenRef.current) return;
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
          const serverErr = typeof data?.error === 'string' ? data.error : undefined;
          const code = typeof data?.code === 'string' ? data.code : '';
          if (res.status === 503 || code.startsWith('openai_tts') || code.startsWith('elevenlabs')) {
            setTtsErrorHint(serverErr || 'Speech output is temporarily unavailable.');
          }
          console.warn('[PublicAgentChat] TTS stream request failed', {
            status: res.status,
            serverError: serverErr,
            code,
            data,
          });
          return;
        }
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = (await res.json()) as {
            success?: boolean;
            audio_base64?: string;
            mime_type?: string;
            error?: string;
            code?: string;
          };
          if (opts.gen !== undefined && opts.gen !== ttsStreamGenRef.current) return;
          if (!data.success || typeof data.audio_base64 !== 'string') {
            const serverErr = typeof data?.error === 'string' ? data.error : 'Speech response invalid.';
            const code = typeof data?.code === 'string' ? data.code : '';
            if (code.startsWith('elevenlabs') || code.startsWith('openai_tts')) {
              setTtsErrorHint(serverErr);
            }
            return;
          }
          setTtsErrorHint(null);
          if (typeof messageIndex === 'number') {
            setTtsLoadingIndex(null);
            setTtsPlayingIndex(messageIndex);
          }
          setPlayingTts(true);
          let binary: Uint8Array;
          try {
            binary = Uint8Array.from(atob(data.audio_base64), (c) => c.charCodeAt(0));
          } catch {
            setPlayingTts(false);
            if (typeof messageIndex === 'number') {
              setTtsLoadingIndex(null);
              setTtsPlayingIndex(null);
            }
            return;
          }
          const blob = new Blob([binary as BlobPart], { type: data.mime_type || 'audio/mpeg' });
          const objectUrl = URL.createObjectURL(blob);
          try {
            const audio = new Audio(objectUrl);
            ttsAudioRef.current = audio;
            audio.setAttribute('playsinline', '');
            await new Promise<void>((resolve) => {
              if (sig.aborted) {
                resolve();
                return;
              }
              const done = () => {
                if (ttsAudioRef.current === audio) ttsAudioRef.current = null;
                resolve();
              };
              audio.onended = done;
              audio.onerror = done;
              void audio.play().catch(() => done());
            });
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        } else {
          setTtsErrorHint(null);
          if (typeof messageIndex === 'number') {
            setTtsLoadingIndex(null);
            setTtsPlayingIndex(messageIndex);
          }
          setPlayingTts(true);

          const pcmReuse: PcmPlaybackReuse | undefined =
            opts.streaming &&
            ttsPcmCtxRef.current &&
            ttsPcmCtxRef.current.state !== 'closed'
              ? { ctx: ttsPcmCtxRef.current, nextTimeRef: ttsPcmNextTimeRef.current }
              : undefined;

          try {
            await playOpenAiSpeechSsePcm({
              body: res.body,
              signal: sig,
              isCancelled: () =>
                opts.gen !== undefined && opts.gen !== ttsStreamGenRef.current,
              onAudioContext: (ctx) => {
                ttsAudioContextRef.current = ctx;
                if (opts.streaming && !pcmReuse) {
                  ttsPcmCtxRef.current = ctx;
                }
              },
              reuse: pcmReuse,
              closeContextAfterPlayback: !opts.streaming,
              timelineOut: opts.streaming && !pcmReuse ? ttsPcmNextTimeRef.current : undefined,
            });
          } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') return;
            console.warn('[PublicAgentChat] TTS stream playback error', e);
          }
        }
      } finally {
        if (!opts.streaming) {
          ttsAudioContextRef.current = null;
        }
        if (opts.gen !== undefined && opts.gen !== ttsStreamGenRef.current) return;
        if (!opts.streaming) {
          setPlayingTts(false);
          if (typeof messageIndex === 'number') {
            setTtsLoadingIndex(null);
            setTtsPlayingIndex(null);
          }
        } else {
          setTtsLoadingIndex(null);
        }
      }
    },
    [pageConfig.app_id, voiceFeatureEnabled, instance?.id]
  );

  const playAssistantTts = useCallback(
    async (
      text: string,
      opts?: { messageIndex?: number; allowWithoutVoiceMode?: boolean }
    ) => {
      if (!voiceFeatureEnabled) return;
      if (!opts?.allowWithoutVoiceMode && !voiceModeRef.current) return;
      if (!voiceTtsEnabledRef.current) return;
      const messageIndex = opts?.messageIndex;
      stopTtsPlayback();
      ttsStreamAbortRef.current = new AbortController();
      await playTtsSegmentInternal(text, {
        messageIndex,
        allowWithoutVoiceMode: opts?.allowWithoutVoiceMode,
        streaming: false,
      });
    },
    [voiceFeatureEnabled, stopTtsPlayback, playTtsSegmentInternal]
  );

  const playQueuedTts = useCallback(async () => {
    if (!voiceTtsEnabledRef.current) {
      setTtsPlaybackPending(null);
      return;
    }
    if (!ttsPlaybackPending) return;
    const { index: pendingIndex, url } = ttsPlaybackPending;
    setTtsPlaybackPending(null);
    setTtsPlayingIndex(pendingIndex);
    setPlayingTts(true);
    try {
      const audio = new Audio(url);
      ttsAudioRef.current = audio;
      audio.setAttribute('playsinline', '');
      await audio.play();
      await new Promise<void>((resolve) => {
        const done = () => {
          if (ttsAudioRef.current === audio) ttsAudioRef.current = null;
          resolve();
        };
        audio.onended = done;
        audio.onerror = done;
      });
    } catch (e) {
      console.warn('[PublicAgentChat] queued TTS play failed', e);
      ttsAudioRef.current = null;
    } finally {
      setTtsPlayingIndex(null);
      setPlayingTts(false);
    }
  }, [ttsPlaybackPending]);

  const handleAssistantBubbleTts = useCallback(
    (messageIndex: number, rawContent: string) => {
      if (ttsLoadingIndex === messageIndex) return;
      if (ttsPlayingIndex === messageIndex) {
        stopTtsPlayback();
        return;
      }
      if (ttsPlaybackPending?.index === messageIndex) {
        void playQueuedTts();
        return;
      }
      void playAssistantTts(rawContent, { messageIndex, allowWithoutVoiceMode: true });
    },
    [ttsLoadingIndex, ttsPlayingIndex, ttsPlaybackPending, stopTtsPlayback, playQueuedTts, playAssistantTts]
  );

  /** Session-only per-message feedback (indices reset when chat is cleared). */
  const [assistantMessageFeedback, setAssistantMessageFeedback] = useState<
    Record<number, 'like' | 'dislike'>
  >({});
  const [assistantCopiedIndex, setAssistantCopiedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (messages.length === 0) {
      setAssistantMessageFeedback({});
      setAssistantCopiedIndex(null);
    }
  }, [messages.length]);

  const handleAssistantFeedback = useCallback((messageIndex: number, kind: 'like' | 'dislike') => {
    setAssistantMessageFeedback((prev) => {
      if (prev[messageIndex] === kind) {
        const next = { ...prev };
        delete next[messageIndex];
        return next;
      }
      return { ...prev, [messageIndex]: kind };
    });
  }, []);

  const copyAssistantText = useCallback(async (messageIndex: number, text: string) => {
    const t = text.trim();
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
      setAssistantCopiedIndex(messageIndex);
      window.setTimeout(() => setAssistantCopiedIndex((prev) => (prev === messageIndex ? null : prev)), 2000);
    } catch {
      /* clipboard may be denied */
    }
  }, []);

  /** Default radio to first enabled tier (avoids e.g. "month" when only "free" exists). */
  useEffect(() => {
    const p = template.pricing || {};
    setBillingPeriod((prev) => {
      if (periodEnabled(p, prev)) return prev;
      const order: BillingPeriod[] = ['free', 'day', 'month', 'year'];
      const first = order.find((x) => periodEnabled(p, x));
      return first ?? prev;
    });
  }, [template.id, template.pricing]);

  useEffect(() => {
    const fields = flattenOnboardingFields(onboardingSections).filter(
      (f) => !f.hidden && f.type !== 'prompt_builder'
    );
    const next: Record<string, unknown> = {};
    for (const field of fields) {
      const key = getOnboardingAnswerKey(field);
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        next[key] = field.defaultValue;
      } else if (field.type === 'boolean') {
        next[key] = false;
      } else if (field.type === 'range') {
        next[key] = field.min ?? 0;
      }
    }
    setAnswers(next);
  }, [template.id, onboardingSections]);

  useEffect(() => {
    setOnboardWizardStep(0);
  }, [template.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [onboardWizardStep]);

  const handleStartOnboard = () => {
    const missing = validateOnboardingRequired(activeOnboardingFields, answers);
    if (missing) {
      alert(`Please fill: ${missing}`);
      return;
    }
    if (!periodEnabled(pricing, billingPeriod)) {
      alert('Select a valid billing period');
      return;
    }
    setShowConfirm(true);
  };

  const handleOnboardWizardNext = useCallback(() => {
    const fieldsForSection = (section: (typeof visibleOnboardingSections)[number]) =>
      section.fields.filter((f) => !f.hidden && f.type !== 'prompt_builder');

    if (onboardWizardStep === 0) {
      if (pricingOnly) return;
      setOnboardWizardStep(sectionCount > 0 ? 1 : billingStepIndex);
      return;
    }
    if (onboardWizardStep >= 1 && onboardWizardStep <= sectionCount) {
      const section = visibleOnboardingSections[onboardWizardStep - 1];
      const missing = validateOnboardingRequired(fieldsForSection(section), answers);
      if (missing) {
        alert(`Please fill: ${missing}`);
        return;
      }
      if (onboardWizardStep === sectionCount) {
        setOnboardWizardStep(billingStepIndex);
      } else {
        setOnboardWizardStep((s) => s + 1);
      }
    }
  }, [onboardWizardStep, sectionCount, billingStepIndex, visibleOnboardingSections, answers, pricingOnly]);

  const handleOnboardWizardBack = useCallback(() => {
    setOnboardWizardStep((s) => Math.max(0, s - 1));
  }, []);

  const confirmOnboard = async () => {
    const cost = creditsForPeriod(pricing, billingPeriod);
    if (cost < 0) {
      alert('Invalid pricing');
      return;
    }
    setSubmitting(true);
    try {
      const onboardingAnswers = await buildOnboardingAnswersForApi(answers, activeOnboardingFields);
      const appId = pageConfig.app_id?.trim() || 'web';
      const { ok, status, data } = await agentOnboard({
        appId,
        agentTemplateId: template.id,
        onboardingAnswers,
        billingPeriod,
      });
      if (ok && data?.success && data.instance) {
        setShowConfirm(false);
        setInstance(data.instance as UserAgentInstance);
        return;
      }
      if (status === 409 && data?.instance_id) {
        const { data: row } = await supabase
          .from('user_agent_instances')
          .select('*')
          .eq('id', data.instance_id)
          .single();
        if (row) {
          setShowConfirm(false);
          setInstance(row as UserAgentInstance);
        }
        return;
      }
      if (data?.error?.includes('Insufficient') || data?.error?.includes('credit') || status === 402) {
        openInsufficientCreditsModalWithAmount(cost || 1);
      } else {
        alert(data?.error || 'Could not start agent');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const sendMessage = useCallback(
    async (textOverride?: string) => {
      const text =
        textOverride !== undefined ? String(textOverride).trim() : input.trim();
      if (textOverride !== undefined) {
        setLastVoiceTranscript('');
        setVoiceModalOpen(false);
      }
      const imgs = pendingImageAttachments.map((p) => p.url);
      debugAgentLog('PublicAgentChat.tsx:sendMessage', 'entry', 'D', {
        textLen: text.length,
        imgsLen: imgs.length,
        hasInstance: !!instance,
      });
      if ((!text && imgs.length === 0) || !instance) {
        debugAgentLog('PublicAgentChat.tsx:sendMessage', 'early_exit', 'D', {
          reason: !instance ? 'no_instance' : 'empty_text_and_no_images',
        });
        return;
      }
      if (instance.status === 'banned') {
        return;
      }
      if (isAgentPlanExpired(instance)) {
        return;
      }
      stickToBottomRef.current = true;
      setSending(true);
      setStreamActivity(null);
      setFileAttachError(null);
      stopTtsPlayback();
      ttsStreamAbortRef.current = new AbortController();
      const ttsStreamGen = ttsStreamGenRef.current;
      assistantStreamAccumRef.current = '';
      ttsPlainOffsetRef.current = 0;
      const assistantMessageIndex = messages.length + 1;
      const userAttachments: UserMessageAttachment[] = [
        ...pendingImageAttachments.map((p) => ({ kind: 'image' as const, url: p.url, filename: p.filename })),
        ...pendingDocumentFilenames.map((filename) => ({ kind: 'document' as const, filename })),
      ];
      setInput('');
      setPendingImageAttachments([]);
      setPendingDocumentFilenames([]);
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: text,
          ...(userAttachments.length ? { attachments: userAttachments } : {}),
        },
      ]);

      let messageParts: AgentChatContentPart[] | undefined;
      if (imgs.length) {
        messageParts = [];
        if (text) messageParts.push({ type: 'text', text });
        for (const url of imgs) {
          messageParts.push({ type: 'image_url', image_url: { url } });
        }
      }

      const appId = pageConfig.app_id?.trim() ?? '';
      if (!appId) {
        setMessages((prev) => [...prev, { role: 'assistant', content: 'App ID missing — set it in Page Settings.' }]);
        setStreamActivity(null);
        setSending(false);
        return;
      }
      const userMsg =
        text ||
        (imgs.length ? (imgs.length === 1 ? 'Describe this image.' : 'Describe these images.') : '');
      streamDeltaBufferRef.current = '';
      streamFlushScheduledRef.current = false;
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
      const { ok, fullText, error, code } = await agentChatStream({
        appId,
        instanceId: instance.id,
        message: userMsg,
        message_parts: messageParts,
        onDelta: (chunk) => {
          if (chunk.length) setStreamActivity(null);
          streamDeltaBufferRef.current += chunk;
          assistantStreamAccumRef.current += chunk;
          if (voiceFeatureEnabled && voiceModeRef.current && voiceTtsEnabledRef.current) {
            const plain = stripMarkdownForSpeech(assistantStreamAccumRef.current);
            while (true) {
              const next = extractNextSpeakableSegment(plain, ttsPlainOffsetRef.current);
              if (!next) break;
              ttsPlainOffsetRef.current = next.newOffset;
              const fetchP = agentTtsStream({
                appId,
                text: next.segment.slice(0, 4000),
                signal: ttsStreamAbortRef.current!.signal,
                instanceId: instance.id,
              });
              ttsPlayChainRef.current = ttsPlayChainRef.current.then(() =>
                playTtsSegmentInternal(
                  { prefetched: fetchP },
                  {
                    messageIndex: assistantMessageIndex,
                    gen: ttsStreamGen,
                    streaming: true,
                  }
                )
              );
            }
          }
          if (streamFlushScheduledRef.current) return;
          streamFlushScheduledRef.current = true;
          queueMicrotask(() => {
            flushPendingStreamDeltas();
          });
        },
        onMeta: (ev) => applyAgentStreamMeta(ev, setStreamActivity),
      });
      flushPendingStreamDeltas();
      if (!ok) {
        stopTtsPlayback();
        setStreamActivity(null);
        if (code === 'agent_plan_expired') {
          void loadInstance();
        } else if (error?.includes('Insufficient') || error?.includes('credit')) {
          openInsufficientCreditsModalWithAmount(instance.credits_per_period || 10);
        }
        setMessages((prev) => {
          const next = [...prev];
          const lastIdx = next.length - 1;
          if (lastIdx >= 0 && next[lastIdx].role === 'assistant') {
            const assistantText =
              code === 'agent_plan_expired'
                ? 'Your plan access has expired. Tap Renew to continue.'
                : error || 'Error';
            next[lastIdx] = { role: 'assistant', content: assistantText };
          }
          return next;
        });
      } else {
        const useVoiceStreamingTts =
          voiceFeatureEnabled && voiceModeRef.current && voiceTtsEnabledRef.current;
        if (useVoiceStreamingTts) {
          const plain = stripMarkdownForSpeech(assistantStreamAccumRef.current);
          const rest = plain.slice(ttsPlainOffsetRef.current).trim();
          if (rest.length >= 12) {
            const fetchRest = agentTtsStream({
              appId,
              text: rest.slice(0, 4000),
              signal: ttsStreamAbortRef.current!.signal,
              instanceId: instance.id,
            });
            ttsPlayChainRef.current = ttsPlayChainRef.current.then(() =>
              playTtsSegmentInternal(
                { prefetched: fetchRest },
                {
                  messageIndex: assistantMessageIndex,
                  gen: ttsStreamGen,
                  streaming: true,
                }
              )
            );
          }
          void ttsPlayChainRef.current.finally(() => {
            if (ttsStreamGen !== ttsStreamGenRef.current) return;
            try {
              void ttsPcmCtxRef.current?.close();
            } catch {
              /* */
            }
            ttsPcmCtxRef.current = null;
            ttsPcmNextTimeRef.current.current = 0;
            ttsAudioContextRef.current = null;
            setPlayingTts(false);
            setTtsLoadingIndex(null);
            setTtsPlayingIndex(null);
          });
        } else {
          void playAssistantTts(fullText || '', { messageIndex: assistantMessageIndex });
        }
        /** If deltas never reached the UI (e.g. SSE parse edge case), still fill the bubble from the final reply. */
        if (fullText?.trim()) {
          setMessages((prev) => {
            const next = [...prev];
            const lastIdx = next.length - 1;
            if (lastIdx >= 0 && next[lastIdx].role === 'assistant' && !next[lastIdx].content.trim()) {
              next[lastIdx] = { role: 'assistant', content: fullText };
            }
            return next;
          });
        }
        /** Stream can finish with no tokens; normalize so the UI shows the fallback instead of a blank bubble. */
        if (!fullText?.trim()) {
          setMessages((prev) => {
            const next = [...prev];
            const lastIdx = next.length - 1;
            if (lastIdx >= 0 && next[lastIdx].role === 'assistant' && !next[lastIdx].content.trim()) {
              next[lastIdx] = { role: 'assistant', content: '(no text)' };
            }
            return next;
          });
        }
      }
      setStreamActivity(null);
      setSending(false);
    },
    [
      input,
      messages,
      pendingImageAttachments,
      pendingDocumentFilenames,
      instance,
      pageConfig.app_id,
      openInsufficientCreditsModalWithAmount,
      playAssistantTts,
      playTtsSegmentInternal,
      stopTtsPlayback,
      flushPendingStreamDeltas,
      voiceFeatureEnabled,
      loadInstance,
    ]
  );

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  const releaseVoiceSttMic = useCallback(() => {
    voiceSttMicStreamRef.current?.getTracks().forEach((t) => t.stop());
    voiceSttMicStreamRef.current = null;
  }, []);

  const finalizeWebSpeechStop = useCallback(() => {
    if (voiceStopDeliveredRef.current) return;
    if (voiceStopSendCancelledRef.current) {
      voiceStopDeliveredRef.current = true;
      return;
    }
    voiceStopDeliveredRef.current = true;

    const snap = voiceStopSnapshotRef.current.trim();
    voiceStopSnapshotRef.current = '';
    const merged = mergeVoiceTranscriptCandidates([
      snap,
      voiceLineRef.current,
      speechFinalsRef.current,
      lastVoiceTranscriptRef.current,
      spokenPrefixRef.current,
    ]).trim();

    speechFinalsRef.current = '';
    voiceLineRef.current = '';
    spokenPrefixRef.current = '';
    setLastVoiceTranscript('');
    setVoiceSpeechInterim(false);
    setIsRecording(false);
    setRecordingTranscribing(false);
    releaseVoiceSttMic();

    if (!merged) {
      setVoiceModalOpen(false);
      return;
    }
    if (!instanceRef.current) {
      alert('Start the agent (complete onboarding) before sending a message.');
      setLastVoiceTranscript(merged);
      setVoiceModalOpen(false);
      return;
    }
    if (instanceRef.current.status === 'banned') {
      setVoiceModalOpen(false);
      return;
    }
    if (isAgentPlanExpired(instanceRef.current)) {
      setVoiceModalOpen(false);
      return;
    }
    void sendMessageRef.current(merged);
  }, [releaseVoiceSttMic]);

  useEffect(() => {
    finalizeWebSpeechStopRef.current = finalizeWebSpeechStop;
  }, [finalizeWebSpeechStop]);

  const stopMediaRecorder = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'recording') {
      try {
        mr.requestData();
      } catch {
        /* not all browsers implement requestData */
      }
    }
    if (mr && mr.state !== 'inactive') {
      try {
        mr.stop();
      } catch {
        /* */
      }
    }
    mediaRecorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setIsRecording(false);
  }, []);

  const stopWebSpeech = useCallback(() => {
    const r = speechRecognitionRef.current;
    if (r) {
      userStoppedListeningRef.current = true;
      try {
        r.stop();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const cancelVoiceSession = useCallback(() => {
    voiceStopSendCancelledRef.current = true;
    voiceStopSnapshotRef.current = '';
    setVoiceModalOpen(false);
    setLastVoiceTranscript('');
    setVoiceSpeechInterim(false);
    speechFinalsRef.current = '';
    voiceLineRef.current = '';
    spokenPrefixRef.current = '';
    skipSendOnEndRef.current = true;
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.abort();
      } catch {
        /* ignore */
      }
      speechRecognitionRef.current = null;
      setIsRecording(false);
      setRecordingTranscribing(false);
    } else if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      voiceCancelRef.current = true;
      stopMediaRecorder();
    }
    releaseVoiceSttMic();
  }, [stopMediaRecorder, releaseVoiceSttMic]);

  const handleClosePanel = useCallback(() => {
    stopTtsPlayback();
    cancelVoiceSession();
    onClosePanel?.();
  }, [stopTtsPlayback, cancelVoiceSession, onClosePanel]);

  useEffect(() => {
    return () => {
      stopTtsPlayback();
    };
  }, [stopTtsPlayback]);

  const startWebSpeech = useCallback(
    async (opts?: { resume?: boolean }) => {
      const resume = opts?.resume ?? false;
      const Ctor = getSpeechRecognitionConstructor();
      if (!Ctor) {
        alert('Speech recognition is not available in this browser.');
        return;
      }
      if (instanceRef.current?.status === 'banned') {
        return;
      }

      if (!resume) {
        if (speechRecognitionRef.current) {
          try {
            speechRecognitionRef.current.abort();
          } catch {
            /* ignore */
          }
          speechRecognitionRef.current = null;
        }
        releaseVoiceSttMic();
        spokenPrefixRef.current = '';
        speechFinalsRef.current = '';
        voiceLineRef.current = '';
        setLastVoiceTranscript('');
        setVoiceSpeechInterim(false);
      }

      if (!voiceSttMicStreamRef.current?.active) {
        try {
          voiceSttMicStreamRef.current = await requestMicrophoneStream();
        } catch {
          alert(
            'Microphone access denied or unavailable. If this app is embedded, the host page must allow the microphone (e.g. iframe allow="microphone").'
          );
          setVoiceModalOpen(false);
          setIsRecording(false);
          return;
        }
      }

      /* Show listening as soon as mic is granted (before recognition.start) so the modal is not stuck on "Ready". */
      setIsRecording(true);

      const recognition = new Ctor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = effectiveSttLocale(template) || DEFAULT_STT_LANG;

      userStoppedListeningRef.current = false;
      speechRecognitionRef.current = recognition;

      recognition.onresult = (event: SpeechRecognitionResultEvent) => {
        if (speechRecognitionRef.current !== recognition) return;
        const finalChunks: string[] = [];
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalChunks.push(event.results[i][0].transcript);
          }
        }
        const instanceMerged = mergeSpeechFinalChunks(finalChunks);
        const fullFinals = spokenPrefixRef.current
          ? `${spokenPrefixRef.current} ${instanceMerged}`.trim()
          : instanceMerged;
        let interim = '';
        for (let i = event.results.length - 1; i >= 0; i--) {
          if (!event.results[i].isFinal) {
            interim = event.results[i][0].transcript;
            break;
          }
        }
        speechFinalsRef.current = fullFinals;
        const line = [fullFinals, interim.trim()].filter(Boolean).join(' ');
        voiceLineRef.current = line;
        setLastVoiceTranscript(line.trim());
        setVoiceSpeechInterim(interim.trim().length > 0);
      };

      recognition.onerror = (e: SpeechRecognitionErrorEvent) => {
        if (speechRecognitionRef.current !== recognition) return;
        /* stop() / abort() emit "aborted" before onend. Do not clear ref here — onend must run so identity check passes and Stop & send can finalize. */
        if (e.error === 'aborted') return;

        speechRecognitionRef.current = null;
        setIsRecording(false);
        setRecordingTranscribing(false);

        const resumeAfterError =
          (e.error === 'no-speech' || e.error === 'network') &&
          (voiceModalOpenRef.current || voiceModeRef.current) &&
          !skipSendOnEndRef.current;
        if (resumeAfterError) {
          spokenPrefixRef.current = speechFinalsRef.current;
          window.setTimeout(() => {
            if (skipSendOnEndRef.current || (!voiceModalOpenRef.current && !voiceModeRef.current)) return;
            if (speechRecognitionRef.current) return;
            void startWebSpeechRef.current({ resume: true });
          }, e.error === 'network' ? 400 : 0);
          return;
        }

        releaseVoiceSttMic();
        setVoiceModalOpen(false);
        if (e.error === 'not-allowed') {
          alert('Microphone access denied for speech recognition.');
        } else if (e.error !== 'service-not-allowed') {
          console.warn('Speech recognition error:', e.error);
        }
      };

      recognition.onend = () => {
        if (speechRecognitionRef.current !== recognition) return;
        setVoiceSpeechInterim(false);
        speechRecognitionRef.current = null;
        const skip = skipSendOnEndRef.current;
        skipSendOnEndRef.current = false;
        const userStop = userStoppedListeningRef.current;
        userStoppedListeningRef.current = false;

        /*
         * Stop & send must win over stale skipSendOnEndRef (e.g. leftover from tap-to-talk reinit).
         * If we handled skip first, we'd clear transcript and set voiceStopSendCancelledRef — finalize never runs.
         */
        if (userStop) {
          queueMicrotask(() => {
            finalizeWebSpeechStopRef.current();
          });
          return;
        }

        if (skip) {
          voiceStopSendCancelledRef.current = true;
          speechFinalsRef.current = '';
          voiceLineRef.current = '';
          spokenPrefixRef.current = '';
          setLastVoiceTranscript('');
          setIsRecording(false);
          setRecordingTranscribing(false);
          releaseVoiceSttMic();
          return;
        }

        if (voiceModalOpenRef.current || voiceModeRef.current) {
          spokenPrefixRef.current = speechFinalsRef.current;
          setRecordingTranscribing(false);
          window.setTimeout(() => {
            if (skipSendOnEndRef.current || (!voiceModalOpenRef.current && !voiceModeRef.current)) return;
            if (speechRecognitionRef.current) return;
            void startWebSpeechRef.current({ resume: true });
          }, 0);
          return;
        }

        speechFinalsRef.current = '';
        voiceLineRef.current = '';
        setIsRecording(false);
        setRecordingTranscribing(false);
        releaseVoiceSttMic();
      };

      try {
        recognition.start();
      } catch (e) {
        console.error(e);
        speechRecognitionRef.current = null;
        setIsRecording(false);
        releaseVoiceSttMic();
        setVoiceModalOpen(false);
        alert('Could not start speech recognition.');
      }
    },
    [releaseVoiceSttMic, template]
  );

  useEffect(() => {
    startWebSpeechRef.current = startWebSpeech;
  }, [startWebSpeech]);

  const startMediaRecorder = useCallback(async (): Promise<boolean> => {
    if (instanceRef.current?.status === 'banned') return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime =
        MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : 'audio/mp4';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) mediaChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        if (voiceCancelRef.current) {
          voiceCancelRef.current = false;
          mediaChunksRef.current = [];
          setRecordingTranscribing(false);
          return;
        }
        const blob = new Blob(mediaChunksRef.current, { type: mr.mimeType });
        mediaChunksRef.current = [];
        if (blob.size < 48) {
          setRecordingTranscribing(false);
          setVoiceModalOpen(false);
          return;
        }
        setRecordingTranscribing(true);
        try {
          const appId = pageConfig.app_id?.trim() || 'web';
          const b64 = await blobToBase64(blob);
          const sttLocaleHint = effectiveSttLocale(template);
          const { ok, data } = await agentTranscribe({
            appId,
            audioBase64: b64,
            mimeType: blob.type,
            ...(sttLocaleHint ? { language: sttLocaleHint } : {}),
            instanceId: instanceRef.current?.id,
          });
          if (ok && data?.text) {
            const t = String(data.text).trim();
            setLastVoiceTranscript(t);
            if (voiceMode && t) {
              void sendMessageRef.current(t);
            } else if (t) {
              setInput((prev) => (prev ? `${prev}\n${t}` : t));
            }
          } else if (!ok) {
            const err = typeof (data as { error?: string })?.error === 'string' ? (data as { error: string }).error : '';
            if (err) {
              alert(err.length > 200 ? `${err.slice(0, 200)}…` : err);
            }
          }
        } finally {
          setRecordingTranscribing(false);
          setVoiceModalOpen(false);
        }
      };
      mediaRecorderRef.current = mr;
      /** No timeslice: one `ondataavailable` on stop — more reliable on iOS Safari than chunked capture. */
      mr.start();
      setIsRecording(true);
      return true;
    } catch (e) {
      console.error(e);
      alert('Microphone access denied or unavailable.');
      return false;
    }
  }, [pageConfig.app_id, voiceMode, template]);

  const handleTapToTalk = useCallback(() => {
    if (instanceRef.current?.status === 'banned') return;
    if (instanceRef.current && isAgentPlanExpired(instanceRef.current)) return;
    stopTtsPlayback();
    if (webSpeechSupported) {
      setVoiceModalOpen(true);
      if (speechRecognitionRef.current) {
        skipSendOnEndRef.current = true;
        try {
          speechRecognitionRef.current.abort();
        } catch {
          /* ignore */
        }
        setIsRecording(false);
        window.setTimeout(() => {
          void startWebSpeech();
        }, 0);
        return;
      }
      void startWebSpeech();
      return;
    }
    void (async () => {
      const started = await startMediaRecorder();
      if (started) setVoiceModalOpen(true);
    })();
  }, [webSpeechSupported, startWebSpeech, startMediaRecorder, stopTtsPlayback]);

  const handleVoiceModalStop = useCallback(
    (transcriptAtClick: string) => {
      if (webSpeechSupported) {
        skipSendOnEndRef.current = false;
        voiceStopDeliveredRef.current = false;
        voiceStopSnapshotRef.current = transcriptAtClick.trim() || lastVoiceTranscriptRef.current.trim();
        voiceStopSendCancelledRef.current = false;
        stopWebSpeech();
        window.setTimeout(() => {
          if (voiceStopDeliveredRef.current) return;
          finalizeWebSpeechStopRef.current();
        }, 500);
        return;
      }
      stopMediaRecorder();
    },
    [webSpeechSupported, stopWebSpeech, stopMediaRecorder]
  );

  const exitVoiceMode = useCallback(() => {
    voiceStopSendCancelledRef.current = true;
    setFileLibraryOpen(false);
    setVoiceModalOpen(false);
    setLastVoiceTranscript('');
    setVoiceSpeechInterim(false);
    spokenPrefixRef.current = '';
    if (speechRecognitionRef.current) {
      skipSendOnEndRef.current = true;
      try {
        speechRecognitionRef.current.abort();
      } catch {
        /* ignore */
      }
      speechRecognitionRef.current = null;
      setIsRecording(false);
      speechFinalsRef.current = '';
      voiceLineRef.current = '';
      setRecordingTranscribing(false);
    } else if (isRecording) {
      voiceCancelRef.current = true;
      stopMediaRecorder();
    }
    setVoiceMode(false);
    releaseVoiceSttMic();
  }, [isRecording, stopMediaRecorder, releaseVoiceSttMic]);

  useEffect(() => {
    return () => {
      skipSendOnEndRef.current = true;
      const r = speechRecognitionRef.current;
      if (r) {
        try {
          r.abort();
        } catch {
          /* ignore */
        }
      }
      speechRecognitionRef.current = null;
      voiceSttMicStreamRef.current?.getTracks().forEach((t) => t.stop());
      voiceSttMicStreamRef.current = null;
    };
  }, []);

  const handleClearMessages = async () => {
    const appId = pageConfig.app_id?.trim() || 'web';
    if (!instance) return;
    setClearingOrDeleting(true);
    try {
      const { ok } = await agentClearMessages({ appId, instanceId: instance.id });
      if (ok) {
        setMessages([]);
        setClearConfirmOpen(false);
        setSettingsOpen(false);
      } else {
        alert('Could not clear conversation');
      }
    } finally {
      setClearingOrDeleting(false);
    }
  };

  const handleDeleteInstance = async () => {
    const appId = pageConfig.app_id?.trim() || 'web';
    if (!instance) return;
    setClearingOrDeleting(true);
    try {
      const { ok } = await agentDeleteInstance({ appId, instanceId: instance.id });
      if (ok) {
        setInstance(null);
        setMessages([]);
        setDeleteConfirmOpen(false);
        setSettingsOpen(false);
      } else {
        alert('Could not delete agent');
      }
    } finally {
      setClearingOrDeleting(false);
    }
  };

  const handleDeleteFileRow = async (fileId: string) => {
    const appId = pageConfig.app_id?.trim() || 'web';
    if (!instance) return;
    const { ok } = await agentDeleteFile({ appId, instanceId: instance.id, fileId });
    if (ok) void refreshInstanceFiles();
  };

  const handleReinstateAfterBan = useCallback(async () => {
    const appId = pageConfig.app_id?.trim() || 'web';
    if (!instance) return;
    setReinstating(true);
    try {
      const { ok, data } = await agentReinstateAfterBan({
        appId,
        instanceId: instance.id,
      });
      const err = extractAgentApiError(data);
      if (!ok) {
        alert(err || 'Could not continue. Try again.');
        return;
      }
      setMessages([]);
      await loadInstance();
      void refreshInstanceFiles();
    } finally {
      setReinstating(false);
    }
  }, [instance, pageConfig.app_id, loadInstance, refreshInstanceFiles]);

  if (loadingInstance) {
    return (
      <div
        className={
          embedInPanel
            ? 'flex h-full min-h-0 w-full items-center justify-center bg-base bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(255,255,255,0.06),transparent_50%),radial-gradient(ellipse_80%_50%_at_100%_50%,rgba(255,255,255,0.04),transparent_45%),radial-gradient(ellipse_60%_40%_at_0%_80%,rgba(255,255,255,0.03),transparent_40%)]'
            : 'min-h-screen flex items-center justify-center bg-base bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(255,255,255,0.06),transparent_50%),radial-gradient(ellipse_80%_50%_at_100%_50%,rgba(255,255,255,0.04),transparent_45%),radial-gradient(ellipse_60%_40%_at_0%_80%,rgba(255,255,255,0.03),transparent_40%)]'
        }
      >
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-subtle/80 bg-elevated/90 shadow-lg shadow-black/20 backdrop-blur-sm">
            <Loader2 className="h-7 w-7 animate-spin text-accent" />
          </div>
          <p className="text-sm text-th-secondary">Loading your assistant…</p>
        </div>
      </div>
    );
  }

  if (!instance) {
    const currentWizardSection =
      onboardWizardStep >= 1 && onboardWizardStep <= sectionCount
        ? visibleOnboardingSections[onboardWizardStep - 1]
        : null;
    const onboardStepDisplay = onboardWizardStep + 1;

    return (
      <div
        className={
          embedInPanel
            ? 'flex h-full min-h-0 max-h-full w-full flex-col items-stretch overflow-x-hidden overflow-y-hidden overscroll-y-contain bg-base bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(255,255,255,0.06),transparent_50%),radial-gradient(ellipse_80%_50%_at_100%_50%,rgba(255,255,255,0.04),transparent_45%)] pb-6 sm:pb-8'
            : 'flex min-h-0 h-[100dvh] max-h-[100dvh] w-full flex-col items-center overflow-x-hidden overflow-y-auto overscroll-y-contain bg-base bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(255,255,255,0.06),transparent_50%),radial-gradient(ellipse_80%_50%_at_100%_50%,rgba(255,255,255,0.04),transparent_45%)] px-4 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(2rem,env(safe-area-inset-top))] sm:px-6 sm:py-10'
        }
      >
        {embedInPanel && onClosePanel ? (
          <div className="relative z-20 flex w-full min-w-0 shrink-0 items-center justify-end border-b border-subtle/60 bg-surface/70 py-2 pl-0 pr-2 backdrop-blur-sm sm:pr-3">
            <button
              type="button"
              onClick={handleClosePanel}
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-subtle/80 bg-elevated/90 py-1.5 pl-2.5 pr-3 text-sm font-medium text-th-secondary transition hover:bg-overlay hover:text-th-primary"
              aria-label="Close panel"
            >
              <X className="h-4 w-4 shrink-0" strokeWidth={2.25} />
              <span>Close</span>
            </button>
          </div>
        ) : null}
        <div
          className={
            embedInPanel
              ? 'flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col gap-3 px-3 pt-3 sm:gap-4 sm:px-4 sm:pt-4'
              : 'flex w-full min-h-0 max-w-lg min-w-0 max-h-full flex-1 shrink-0 flex-col gap-4'
          }
        >
          {onboardWizardStep === 0 && !pricingOnly ? <PublicAgentCoverMedia template={template} /> : null}
          <div
            className={
              embedInPanel
                ? 'relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-3xl border border-subtle/80 bg-elevated/90 shadow-2xl shadow-black/30 backdrop-blur-md'
                : 'relative flex min-h-0 w-full min-w-0 max-h-[min(46rem,85dvh)] flex-1 flex-col overflow-hidden rounded-3xl border border-subtle/80 bg-elevated/90 shadow-2xl shadow-black/30 backdrop-blur-md sm:max-h-[min(52rem,88dvh)]'
            }
          >
            <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto overscroll-y-contain scroll-smooth p-5 pb-3 sm:p-7 sm:pb-3">
              <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-medium uppercase tracking-wide text-th-secondary">
                  Step {onboardStepDisplay} of {onboardTotalSteps}
                </span>
                <span className="text-xs text-th-tertiary tabular-nums">
                  {Math.round((onboardStepDisplay / onboardTotalSteps) * 100)}%
                </span>
              </div>
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-subtle/80"
                role="progressbar"
                aria-valuenow={onboardStepDisplay}
                aria-valuemin={1}
                aria-valuemax={onboardTotalSteps}
              >
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand to-accent transition-[width] duration-300 ease-out"
                  style={{ width: `${(onboardStepDisplay / onboardTotalSteps) * 100}%` }}
                />
              </div>
            </div>

            {onboardWizardStep === 0 && pricingOnly && (
              <div className="space-y-3">
                <h1 className="text-2xl font-bold text-th-primary">{displayTitle}</h1>
                {displayDescription ? (
                  <p className="text-th-secondary leading-relaxed">{displayDescription}</p>
                ) : null}
                <p className="text-sm text-th-secondary">Choose your plan to get started.</p>
              </div>
            )}

            {onboardWizardStep === 0 && !pricingOnly && (
              <div className="space-y-3">
                <h1 className="text-2xl font-bold text-th-primary">{displayTitle}</h1>
                {displayDescription ? (
                  <p className="text-th-secondary leading-relaxed">{displayDescription}</p>
                ) : null}
                <p className="text-sm text-th-secondary">
                  {sectionCount > 0
                    ? 'Answer a few short questions, then pick your plan to get started.'
                    : 'Choose your plan below to get started.'}
                </p>
              </div>
            )}

            {currentWizardSection && (
              <div key={currentWizardSection.id} className="space-y-4">
                <h2 className="text-base font-semibold text-th-primary border-b border-subtle pb-2">
                  {currentWizardSection.title}
                </h2>
                {currentWizardSection.fields
                  .filter((f) => !f.hidden)
                  .map((field) => {
                    const key = getOnboardingAnswerKey(field);
                    if (field.type === 'prompt_builder') {
                      return (
                        <div
                          key={field.id}
                          className="rounded-lg border border-status-warning/30 bg-status-warning/10 p-3 text-sm text-th-secondary"
                        >
                          <span className="font-medium text-th-primary">{field.label}</span> — Prompt builder is not
                          supported on the public signup form. Use text or textarea fields instead.
                        </div>
                      );
                    }
                    const val = onboardingFieldValue(field, answers);
                    return (
                      <div key={field.id} className="space-y-1">
                        {field.label && field.type !== 'boolean' && (
                          <label className="block text-sm font-medium text-th-primary">
                            {field.label}
                            {field.required && <span className="text-status-error ml-1">*</span>}
                          </label>
                        )}
                        <FormFieldRenderer
                          field={field}
                          value={val}
                          hasError={false}
                          disabled={false}
                          onChange={(value) =>
                            setAnswers((prev) => ({
                              ...prev,
                              [key]: value,
                            }))
                          }
                        />
                        {field.description && (
                          <p className="text-xs text-th-secondary mt-1">{field.description}</p>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {onboardWizardStep === billingStepIndex && (
              <div className="space-y-2 sm:space-y-3">
                <h2 className="text-sm font-semibold text-th-primary">Choose your plan</h2>
                <div
                  className="flex w-full min-w-0 flex-col gap-2"
                  role="radiogroup"
                  aria-label="Billing plan"
                >
                  {BILLING_PERIOD_UI_ORDER.map((p) => {
                    if (!periodEnabled(pricing, p)) return null;
                    const selected = billingPeriod === p;
                    return (
                      <label
                        key={p}
                        className={`relative flex w-full min-w-0 cursor-pointer flex-row items-center gap-2 rounded-2xl border p-2.5 text-left transition sm:min-h-[4.5rem] sm:gap-3 sm:p-3 ${
                          selected
                            ? 'border-brand/70 bg-gradient-to-br from-brand/15 via-brand/5 to-accent/10 shadow-[0_0_0_1px_rgba(255,255,255,0.18)] ring-2 ring-brand/25'
                            : 'border-subtle/80 bg-elevated/50 hover:border-subtle hover:bg-elevated/80'
                        }`}
                      >
                        <input
                          type="radio"
                          name="bp"
                          className="sr-only"
                          checked={selected}
                          onChange={() => setBillingPeriod(p)}
                        />
                        <span
                          className={`flex h-14 w-14 shrink-0 flex-col items-center justify-center overflow-hidden rounded-2xl border font-semibold tabular-nums leading-none sm:h-16 sm:w-16 ${
                            selected
                              ? 'border-brand/50 bg-brand/30 text-white shadow-inner shadow-black/20'
                              : 'border-subtle/70 bg-surface/80 text-th-primary'
                          } ${p === 'free' ? '' : 'gap-1 py-1.5'}`}
                          aria-hidden
                        >
                          {p === 'free' ? (
                            <Gift
                              className={`h-8 w-8 shrink-0 sm:h-9 sm:w-9 ${selected ? 'text-white' : 'text-th-primary'}`}
                            />
                          ) : (
                            <>
                              <span
                                className={`text-center text-lg font-bold leading-none tracking-tight sm:text-xl ${selected ? 'text-white' : 'text-th-primary'}`}
                              >
                                {billingCreditBadgeText(pricing, p)}
                              </span>
                              <span
                                className={`text-[10px] font-semibold uppercase leading-tight tracking-wide sm:text-xs ${selected ? 'text-white/90' : 'text-th-secondary'}`}
                              >
                                credits
                              </span>
                            </>
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="line-clamp-2 text-xs font-semibold leading-tight text-th-primary sm:text-sm">
                            {billingPeriodLabel(p)}
                          </span>
                          <span className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-th-secondary sm:line-clamp-3 sm:text-[11px]">
                            {billingPeriodBlurb(pricing, p)}
                          </span>
                        </div>
                        {selected && (
                          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand shadow-[0_0_6px_rgba(255,255,255,0.35)] sm:right-2 sm:top-2" />
                        )}
                      </label>
                    );
                    })}
                </div>
              </div>
            )}

            {onboardWizardStep === 0 && !pricingOnly ? <MultilingualTagline variant="banner" /> : null}

              </div>
            </div>

            <div
              className={`shrink-0 border-t border-subtle/60 bg-elevated/95 px-4 py-3 backdrop-blur-md sm:px-7 sm:py-3.5 ${
                embedInPanel ? 'pb-[max(0.75rem,env(safe-area-inset-bottom))]' : ''
              }`}
            >
              <div
                className={`flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3 ${
                  onboardWizardStep > 0 ? 'sm:justify-between' : ''
                }`}
              >
                {onboardWizardStep > 0 ? (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-subtle/80 bg-elevated/60 px-4 py-3 text-sm font-medium text-th-secondary transition hover:bg-overlay hover:text-th-primary sm:min-w-[7rem] sm:shrink-0"
                    onClick={handleOnboardWizardBack}
                  >
                    <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
                    Back
                  </button>
                ) : null}
                {onboardWizardStep < billingStepIndex ? (
                  <button
                    type="button"
                    className={`button-primary flex items-center justify-center gap-2 py-3 ${onboardWizardStep === 0 ? 'w-full' : 'w-full sm:flex-1'}`}
                    onClick={handleOnboardWizardNext}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                  </button>
                ) : (
                  <button type="button" className="button-primary w-full py-3 sm:flex-1" onClick={handleStartOnboard}>
                    Continue
                  </button>
                )}
              </div>
            </div>
          </div>

        </div>

        {showConfirm && (
          <div className={`${layer} inset-0 z-modal-content flex items-center justify-center bg-black/60 p-4`}>
            <div className="bg-surface border border-subtle rounded-xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-lg font-semibold text-th-primary">Confirm credits</h3>
              <p className="text-th-secondary text-sm">
                {billingPeriod === 'free'
                  ? 'This plan is free (ad-supported). No credits will be charged.'
                  : `This will deduct ${creditsForPeriod(pricing, billingPeriod).toLocaleString()} credits for ${billingPeriodAccessAdjective(billingPeriod)} access to ${template.name}.`}
              </p>
              <div className="flex gap-2 justify-end">
                <button type="button" className="button-secondary" onClick={() => setShowConfirm(false)}>
                  Cancel
                </button>
                <button type="button" className="button-primary" disabled={submitting} onClick={confirmOnboard}>
                  {submitting ? 'Submitting…' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        <ModalContainer />
      </div>
    );
  }

  return (
    <div
      className={
        embedInPanel
          ? 'relative flex h-full min-h-0 w-full max-h-full flex-col overflow-hidden bg-base'
          : 'relative flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-base'
      }
    >
      <div
        className={`pointer-events-none ${layer} inset-0 bg-[radial-gradient(ellipse_100%_60%_at_50%_-10%,rgba(255,255,255,0.05),transparent_55%),radial-gradient(ellipse_70%_45%_at_100%_30%,rgba(255,255,255,0.04),transparent_50%),radial-gradient(ellipse_50%_35%_at_0%_85%,rgba(255,255,255,0.03),transparent_45%)]`}
        aria-hidden
      />
      {vadActive && (
        <div
          className={`pointer-events-none ${layer} inset-0 z-[4] bg-[radial-gradient(ellipse_80%_60%_at_50%_100%,rgba(255,255,255,0.08),transparent_55%),radial-gradient(ellipse_60%_50%_at_30%_50%,rgba(255,255,255,0.05),transparent_50%)] animate-pulse`}
          aria-hidden
        />
      )}

      <header
        className={`${embedInPanel ? 'absolute' : 'fixed'} left-0 right-0 top-0 z-header shrink-0 border-b border-subtle/60 bg-surface/70 backdrop-blur-xl transition-transform duration-200 ease-out ${
          headerVisible ? 'translate-y-0' : '-translate-y-full pointer-events-none'
        }`}
      >
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-3 py-3 sm:gap-3 sm:px-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-subtle/80 bg-gradient-to-br from-brand/35 to-accent/25 shadow-inner">
            {template.cover_media_url?.trim() && template.cover_media_type !== 'video' ? (
              <img
                src={template.cover_media_url.trim()}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <Sparkles className="h-5 w-5 text-th-primary" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-base font-semibold tracking-tight text-th-primary sm:text-lg">
                {displayTitle}
              </h1>
              {instance.next_billing_at && (
                <span className="inline-flex max-w-full items-center rounded-full border border-subtle/80 bg-elevated/90 px-2.5 py-0.5 text-[11px] font-medium text-th-secondary">
                  <span className="truncate">
                    Access valid until · {new Date(instance.next_billing_at).toLocaleString()}
                  </span>
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-subtle/80 bg-elevated/90 text-th-secondary transition hover:bg-overlay hover:text-th-primary"
              aria-label="Settings"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="h-5 w-5" />
            </button>
            {embedInPanel && onClosePanel ? (
              <button
                type="button"
                onClick={handleClosePanel}
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-subtle/80 bg-elevated/90 py-1.5 pl-2.5 pr-3 text-sm font-medium text-th-secondary transition hover:bg-overlay hover:text-th-primary"
                aria-label="Close panel"
              >
                <X className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                <span>Close</span>
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {showPlanExpiredModal && instance && (
        <AgentPlanExpiredModal
          open
          instance={instance}
          renewing={renewingPlan}
          onRenew={handleRenewPlan}
          onOpenSettings={() => setSettingsOpen(true)}
          embedInPanel={embedInPanel}
        />
      )}

      {isBannedInstance && (
        <div
          className={`pointer-events-none ${layer} inset-0 z-[35] flex items-center justify-center p-4 sm:p-6`}
          aria-live="polite"
        >
          <div
            role="alert"
            className="pointer-events-auto w-full max-w-md rounded-xl border border-status-warning/45 bg-status-warning/10 px-4 py-4 text-center text-sm text-th-secondary shadow-xl shadow-black/25 backdrop-blur-md"
          >
            <p className="font-medium text-status-warning">This assistant was temporarily deactivated.</p>
            <p className="mt-1.5 leading-relaxed">
              Tap Continue to clear your conversation and uploaded files and resume chatting. Your setup and
              personalization stay saved.
            </p>
            <p className="mt-2 text-xs text-th-tertiary tabular-nums">
              Messages in chat: {messages.length} · Files in library: {instanceFiles.length}
            </p>
            <button
              type="button"
              disabled={reinstating}
              onClick={() => void handleReinstateAfterBan()}
              className="mt-4 w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-surface transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              {reinstating ? 'Continuing…' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      <main
        className={`relative z-10 flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-visible transition-[padding-top] duration-200 ease-out ${
          headerVisible ? 'pt-16' : 'pt-0'
        }`}
      >
        <div
          ref={scrollRef}
          onScroll={onConversationScroll}
          onWheel={onConversationWheel}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-6 sm:px-6"
        >
          <div className="mx-auto flex max-w-2xl flex-col gap-5">
            {messages.length === 0 && !sending && (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center sm:py-16">
                {template.cover_media_url?.trim() ? (
                  <div className="mb-6 w-full max-w-sm overflow-hidden rounded-3xl border border-subtle/60 bg-elevated/40 aspect-video">
                    {template.cover_media_type === 'video' ? (
                      <video
                        src={template.cover_media_url.trim()}
                        className="h-full w-full object-cover"
                        controls
                        playsInline
                      />
                    ) : (
                      <img
                        src={template.cover_media_url.trim()}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>
                ) : (
                  <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-3xl border border-subtle/60 bg-elevated/40 backdrop-blur-sm">
                    <Sparkles className="h-8 w-8 text-accent/90" />
                  </div>
                )}
                <h2 className="mb-2 text-xl font-medium tracking-tight text-th-primary sm:text-2xl">
                  {displayTitle}
                </h2>
                {displayDescription ? (
                  <p className="max-w-md text-sm leading-relaxed text-th-secondary">{displayDescription}</p>
                ) : null}
                <div className="mt-6 w-full">
                  <MultilingualTagline variant="banner" />
                </div>
              </div>
            )}

            {messages.map((m, i) => {
              const bubbleJustAppeared =
                i === messages.length - 1 ||
                (m.role === 'user' && i === messages.length - 2 && sending);
              const isStreamingAssistant = m.role === 'assistant' && i === messages.length - 1 && sending;
              return (
              <div
                key={i}
                ref={isStreamingAssistant ? lastStreamingAssistantRef : undefined}
                className={`flex w-full ${m.role === 'user' ? 'justify-end' : 'justify-start'} ${
                  bubbleJustAppeared ? 'chat-bubble-enter' : ''
                } ${isStreamingAssistant ? 'scroll-mt-2 sm:scroll-mt-3' : ''}`}
              >
                <div
                  className={
                    m.role === 'user'
                      ? 'max-w-[min(92%,560px)] rounded-[1.35rem] border border-brand/20 bg-brand/12 px-4 py-3 text-[15px] leading-relaxed text-th-primary shadow-sm transition-shadow duration-300'
                      : 'max-w-[min(95%,680px)] rounded-[1.25rem] border border-subtle/40 bg-elevated/70 px-4 py-3 font-chat text-[15px] leading-relaxed text-th-primary shadow-sm backdrop-blur-sm transition-[box-shadow,background-color] duration-300'
                  }
                >
                  {m.role === 'user' ? (
                    <UserMessageBubble message={m} />
                  ) : (
                    <>
                      <div className="min-w-0 break-words">
                        <AssistantMessageBubble
                          content={m.content}
                          isLastAssistant={i === messages.length - 1}
                          sending={sending}
                          activityHint={
                            m.role === 'assistant' && i === messages.length - 1 && sending
                              ? streamActivity
                              : null
                          }
                        />
                      </div>
                      {m.content.trim() &&
                        m.content.trim() !== '(no text)' &&
                        !(sending && i === messages.length - 1) && (
                          <div
                            className="chat-toolbar-enter mt-3 flex flex-wrap items-center gap-x-0.5 gap-y-1 border-t border-subtle/45 pt-2.5"
                            role="toolbar"
                            aria-label="Message actions"
                          >
                            {voiceFeatureEnabled && voiceTtsEnabled && (
                              <button
                                type="button"
                                className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${
                                  ttsPlaybackPending?.index === i || ttsPlayingIndex === i
                                    ? 'text-accent'
                                    : 'text-th-secondary hover:bg-overlay hover:text-th-primary'
                                }`}
                                aria-label={
                                  ttsPlaybackPending?.index === i
                                    ? 'Tap to play speech (browser blocked autoplay)'
                                    : ttsPlayingIndex === i
                                      ? 'Stop speech'
                                      : ttsLoadingIndex === i
                                        ? 'Loading speech'
                                        : 'Speak this message'
                                }
                                onClick={() => handleAssistantBubbleTts(i, m.content)}
                              >
                                {ttsLoadingIndex === i ? (
                                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                                ) : (
                                  <Volume2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                                )}
                                <span>Speak</span>
                              </button>
                            )}
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${
                                assistantMessageFeedback[i] === 'like'
                                  ? 'text-accent'
                                  : 'text-th-secondary hover:bg-overlay hover:text-th-primary'
                              }`}
                              aria-pressed={assistantMessageFeedback[i] === 'like'}
                              aria-label="Like"
                              onClick={() => handleAssistantFeedback(i, 'like')}
                            >
                              <ThumbsUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              <span>Like</span>
                            </button>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${
                                assistantMessageFeedback[i] === 'dislike'
                                  ? 'text-status-error'
                                  : 'text-th-secondary hover:bg-overlay hover:text-th-primary'
                              }`}
                              aria-pressed={assistantMessageFeedback[i] === 'dislike'}
                              aria-label="Dislike"
                              onClick={() => handleAssistantFeedback(i, 'dislike')}
                            >
                              <ThumbsDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              <span>Dislike</span>
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-th-secondary transition hover:bg-overlay hover:text-th-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                              aria-label="Copy message"
                              onClick={() => void copyAssistantText(i, m.content)}
                            >
                              {assistantCopiedIndex === i ? (
                                <Check className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
                              ) : (
                                <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              )}
                              <span>{assistantCopiedIndex === i ? 'Copied' : 'Copy'}</span>
                            </button>
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="relative z-20 shrink-0 overflow-visible border-t border-subtle/50 bg-surface/85 backdrop-blur-xl px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 sm:px-5 sm:pb-[max(0.65rem,env(safe-area-inset-bottom))] sm:pt-2">
          <div className="mx-auto max-w-2xl">
            {fileAttachError && (
              <div
                className="mb-2 flex items-start justify-between gap-2 rounded-xl border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error"
                role="alert"
              >
                <span className="min-w-0">{fileAttachError}</span>
                <button
                  type="button"
                  className="shrink-0 text-th-secondary hover:text-th-primary"
                  aria-label="Dismiss"
                  onClick={() => setFileAttachError(null)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {voiceFeatureEnabled && ttsErrorHint && (
              <div
                className="mb-2 flex items-start justify-between gap-2 rounded-xl border border-status-warning/35 bg-status-warning/10 px-3 py-2 text-sm text-th-secondary"
                role="status"
              >
                <span className="min-w-0 leading-snug">{ttsErrorHint}</span>
                <button
                  type="button"
                  className="shrink-0 text-th-secondary hover:text-th-primary"
                  aria-label="Dismiss"
                  onClick={() => setTtsErrorHint(null)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {pendingImageAttachments.length > 0 && template.file_uploads_enabled && (
              <div className="mb-3 flex flex-wrap gap-3">
                {pendingImageAttachments.map((att, idx) => (
                  <div
                    key={`${idx}-${att.url.slice(0, 32)}`}
                    className="flex max-w-[104px] flex-col items-center gap-1"
                  >
                    <div className="relative rounded-xl border border-subtle/60 bg-elevated/50 p-1">
                      <img
                        src={att.url}
                        alt=""
                        className="h-20 w-20 rounded-lg border border-subtle/80 object-cover"
                      />
                      <button
                        type="button"
                        className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full border border-subtle/80 bg-surface text-th-secondary shadow"
                        onClick={() => setPendingImageAttachments((prev) => prev.filter((_, i) => i !== idx))}
                        aria-label={`Remove ${att.filename}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span
                      className="w-full truncate text-center text-[11px] leading-tight text-th-secondary"
                      title={att.filename}
                    >
                      {att.filename}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {voiceFeatureEnabled && voiceTtsEnabled && ttsPlaybackPending && (
              <div className="mb-2 flex items-center justify-center gap-2 rounded-xl border border-accent/40 bg-elevated/95 px-3 py-2.5 text-left text-sm shadow-sm backdrop-blur-md">
                <Volume2 className="h-4 w-4 shrink-0 text-accent" aria-hidden />
                <span className="min-w-0 flex-1 text-th-secondary">
                  Playback needs a tap (browser audio policy). Tap Play to hear the reply.
                </span>
                <button
                  type="button"
                  onClick={() => void playQueuedTts()}
                  className="shrink-0 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-surface shadow-md shadow-black/20 transition hover:bg-brand-hover"
                >
                  Play
                </button>
              </div>
            )}

            {voiceFeatureEnabled && voiceMode ? (
              <div className="rounded-2xl border border-brand/30 bg-elevated/90 px-1.5 py-1.5 shadow-sm shadow-black/15 backdrop-blur-md sm:px-2.5 sm:py-2">
                <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-x-1.5 gap-y-1 sm:gap-x-2">
                  <div className="flex min-w-0 flex-wrap items-center justify-self-start gap-1.5">
                    <button
                      type="button"
                      onClick={exitVoiceMode}
                      className="flex shrink-0 items-center gap-1 rounded-full border border-subtle/80 bg-surface/80 px-2 py-1 text-[11px] font-medium text-th-primary transition hover:bg-overlay"
                      title="Type instead"
                      aria-label="Type instead"
                    >
                      <Keyboard className="h-3.5 w-3.5" />
                      <span>Type</span>
                    </button>
                  </div>

                  <div className="flex justify-center justify-self-center">
                    <button
                      type="button"
                      onClick={() => void handleTapToTalk()}
                      disabled={voiceModalOpen || sending || isBannedInstance || planExpired}
                      className="group relative isolate flex min-h-[48px] shrink-0 items-center justify-center gap-2.5 rounded-full bg-gradient-to-br from-brand via-brand to-brand-hover px-5 py-2.5 text-sm font-semibold tracking-tight text-surface shadow-[0_0_28px_rgba(255,255,255,0.12)] ring-2 ring-subtle/40 transition hover:brightness-110 active:scale-[0.98] active:brightness-95 disabled:cursor-not-allowed disabled:opacity-45 sm:min-h-[52px] sm:gap-3 sm:px-7 sm:py-3 sm:text-base"
                      aria-label="Tap to talk — opens voice capture"
                      title="Tap to talk"
                    >
                      {!voiceModalOpen && (
                        <span
                          className="pointer-events-none absolute inset-[-6px] rounded-full bg-brand/35 blur-2xl motion-safe:animate-pulse"
                          aria-hidden
                        />
                      )}
                      <Mic className="relative z-10 h-6 w-6 shrink-0 drop-shadow sm:h-7 sm:w-7" />
                      <span className="relative z-10 max-w-[11rem] leading-tight sm:max-w-none">Tap to talk</span>
                    </button>
                  </div>

                  <div className="flex min-w-0 flex-wrap items-center justify-end justify-self-end gap-1">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={voiceTtsEnabled}
                      aria-label={voiceTtsEnabled ? 'Spoken replies on. Press to mute assistant voice.' : 'Spoken replies off. Press to hear assistant voice.'}
                      title={voiceTtsEnabled ? 'Spoken replies on — click to mute' : 'Spoken replies off — click to hear answers'}
                      onClick={() => {
                        setVoiceTtsEnabled((prev) => {
                          const next = !prev;
                          if (!next) stopTtsPlayback();
                          return next;
                        });
                      }}
                      className={`flex max-w-full shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium transition sm:gap-1.5 sm:px-2.5 sm:py-1.5 ${
                        voiceTtsEnabled
                          ? 'border-brand/45 bg-brand/12 text-th-primary shadow-[0_0_0_1px_rgba(255,255,255,0.1)]'
                          : 'border-subtle/80 bg-surface/80 text-th-secondary hover:bg-overlay hover:text-th-primary'
                      }`}
                    >
                      {voiceTtsEnabled ? (
                        <Volume2 className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
                      ) : (
                        <VolumeX className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
                      )}
                      <span className="hidden min-w-0 truncate sm:inline">Speak</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative flex flex-col gap-0 overflow-hidden rounded-[32px] border border-subtle/70 bg-elevated/92 shadow-lg shadow-black/15 backdrop-blur-md focus-within:border-brand/35 focus-within:ring-2 focus-within:ring-brand/12">
                {template.file_uploads_enabled &&
                  (pendingImageAttachments.length > 0 || pendingDocumentFilenames.length > 0) && (
                    <div
                      className="flex flex-wrap items-center gap-1.5 border-b border-subtle/50 px-3 py-2"
                      aria-label="Attached files"
                    >
                      {pendingImageAttachments.map((att, idx) => (
                        <span
                          key={`bar-img-${idx}-${att.filename}`}
                          className="inline-flex max-w-[min(168px,42vw)] items-center gap-1 rounded-full border border-brand/25 bg-brand/10 px-2 py-1 text-[11px] font-medium text-th-primary"
                          title={att.filename}
                        >
                          <span className="truncate">{att.filename}</span>
                        </span>
                      ))}
                      {pendingDocumentFilenames.map((name, idx) => (
                        <span
                          key={`bar-doc-${idx}-${name}`}
                          className="inline-flex max-w-[min(168px,42vw)] items-center gap-1 rounded-full border border-subtle/70 bg-surface/70 px-2 py-1 text-[11px] font-medium text-th-primary"
                          title={name}
                        >
                          <FileText className="h-3 w-3 shrink-0 text-th-secondary" aria-hidden />
                          <span className="truncate">{name}</span>
                        </span>
                      ))}
                    </div>
                  )}
                <div className="flex items-end gap-1 p-1.5 pl-2 sm:gap-2 sm:pl-3">
                {template.file_uploads_enabled && (
                  <div className="relative flex shrink-0 items-end pb-1">
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-full text-th-secondary transition hover:bg-overlay hover:text-th-primary disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="File library — upload and manage files"
                      onClick={openFileLibrary}
                      disabled={isBannedInstance || planExpired}
                    >
                      <FolderOpen className="h-6 w-6" />
                    </button>
                  </div>
                )}
                <textarea
                  className="max-h-[200px] min-h-[48px] flex-1 resize-none bg-transparent py-2.5 text-[15px] leading-snug text-th-primary placeholder:text-th-tertiary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Message…"
                  rows={1}
                  value={input}
                  disabled={isBannedInstance || planExpired}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void sendMessage();
                    }
                  }}
                />
                {voiceFeatureEnabled ? (
                  <button
                    type="button"
                    className="mb-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-subtle/80 text-th-secondary transition hover:bg-overlay hover:text-brand disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() => setVoiceMode(true)}
                    disabled={isBannedInstance || planExpired}
                    aria-label="Voice input"
                    title="Voice input"
                  >
                    <Mic className="h-5 w-5" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="mb-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-surface shadow-md shadow-black/20 transition duration-200 hover:bg-brand-hover active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100"
                  disabled={
                    isBannedInstance ||
                    planExpired ||
                    sending ||
                    (!input.trim() &&
                      pendingImageAttachments.length === 0 &&
                      pendingDocumentFilenames.length === 0)
                  }
                  onClick={() => void sendMessage()}
                  aria-label="Send message"
                >
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                </button>
                </div>
              </div>
            )}
            <p className="mt-1 text-center text-[10px] leading-tight text-th-tertiary sm:text-[11px]">
              <span className="inline-flex flex-wrap items-center justify-center gap-x-1">
                <button
                  type="button"
                  className="font-medium text-brand hover:underline"
                  onClick={() => openModal('disclaimer')}
                >
                  Disclaimer
                </button>
                <span className="text-th-tertiary/40" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  className="font-medium text-brand hover:underline"
                  onClick={() => openModal('privacy')}
                >
                  Privacy Policy
                </button>
                <span className="text-th-tertiary/40" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  className="font-medium text-brand hover:underline"
                  onClick={() => openModal('terms')}
                >
                  Terms of Service
                </button>
              </span>
            </p>
          </div>
        </div>
      </main>

      {settingsOpen && (
        <div className={`${layer} inset-0 z-modal-content flex items-end justify-center sm:items-center sm:p-4`}>
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Close settings"
            onClick={() => setSettingsOpen(false)}
          />
          <div className="relative z-10 max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-subtle/80 bg-surface p-5 shadow-2xl sm:rounded-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-th-primary">Settings</h2>
              <button
                type="button"
                className="rounded-full p-2 text-th-secondary hover:bg-overlay"
                onClick={() => setSettingsOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <section className="mb-6 space-y-2">
              <h3 className="text-sm font-medium text-th-primary">Memory</h3>
              <p className="text-xs text-th-secondary">
                {messages.length} message{messages.length === 1 ? '' : 's'} stored for this agent.
              </p>
              <button
                type="button"
                className="button-secondary text-sm"
                onClick={() => setClearConfirmOpen(true)}
              >
                Clear conversation
              </button>
            </section>

            {template.file_uploads_enabled && (
              <section className="mb-6 space-y-2">
                <h3 className="text-sm font-medium text-th-primary">Your files</h3>
                {loadingFiles ? (
                  <Loader2 className="h-5 w-5 animate-spin text-brand" />
                ) : instanceFiles.length === 0 ? (
                  <p className="text-xs text-th-tertiary">No files uploaded yet.</p>
                ) : (
                  <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                    {instanceFiles.map((f) => (
                      <li
                        key={f.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-subtle/60 px-2 py-1.5"
                      >
                        <span className="truncate text-th-primary">{f.filename}</span>
                        <button
                          type="button"
                          className="shrink-0 text-status-error hover:underline"
                          onClick={() => void handleDeleteFileRow(f.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            <section className="space-y-2 border-t border-subtle/60 pt-4">
              <h3 className="text-sm font-medium text-status-error">Danger zone</h3>
              <p className="text-xs text-th-secondary">
                Delete this agent subscription, chat history, and uploaded files. You can onboard again later.
              </p>
              <button
                type="button"
                className="rounded-xl border border-status-error/50 bg-status-error/10 px-4 py-2 text-sm font-medium text-status-error hover:bg-status-error/20"
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete agent
              </button>
            </section>
          </div>
        </div>
      )}

      {template.file_uploads_enabled && fileLibraryOpen && (
        <div
          className={`${layer} inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-4`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="file-library-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Close file library"
            onClick={() => setFileLibraryOpen(false)}
          />
          <div
            className="relative z-10 mt-auto w-full max-h-[85vh] overflow-hidden rounded-t-2xl border border-subtle bg-surface shadow-2xl sm:mt-0 sm:max-h-[min(90vh,560px)] sm:max-w-md sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-subtle px-4 py-3">
              <h2 id="file-library-title" className="flex items-center gap-2 text-lg font-semibold text-th-primary">
                <FolderOpen className="h-5 w-5 shrink-0 text-brand" aria-hidden />
                File library
              </h2>
              <button
                type="button"
                className="rounded-full p-2 text-th-secondary hover:bg-overlay"
                aria-label="Close"
                onClick={() => setFileLibraryOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[min(60vh,400px)] space-y-4 overflow-y-auto px-4 py-4">
              <p className="text-sm text-th-secondary">
                Upload from your device. Text and documents are inserted into the message; images are added as attachments for your next send.
              </p>
              {fileAttachError ? (
                <div
                  role="alert"
                  className="rounded-lg border border-status-error/50 bg-status-error/10 px-3 py-2 text-sm text-status-error"
                >
                  {fileAttachError}
                </div>
              ) : null}
              {/* `display:none` breaks file pickers on iOS/Safari; use an in-flow invisible overlay. */}
              <label className="button-primary relative flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-2">
                <input
                  type="file"
                  className="absolute inset-0 z-[1] h-full w-full cursor-pointer opacity-0"
                  accept=".txt,text/plain,.pdf,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                  multiple
                  onChange={onLibraryFilesChange}
                />
                <Upload className="pointer-events-none h-4 w-4 shrink-0" aria-hidden />
                <span className="pointer-events-none">Upload from device</span>
              </label>
              <div className="border-t border-subtle pt-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-th-tertiary">Your uploads</h3>
                {loadingFiles ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-brand" />
                  </div>
                ) : instanceFiles.length === 0 ? (
                  <p className="text-sm text-th-tertiary">No files stored yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {instanceFiles.map((f) => (
                      <li
                        key={f.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-subtle/60 bg-elevated/50 px-3 py-2 text-sm"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0 text-th-secondary" aria-hidden />
                          <span className="truncate text-th-primary" title={f.filename}>
                            {f.filename}
                          </span>
                        </span>
                        <button
                          type="button"
                          className="shrink-0 rounded-md p-1.5 text-status-error hover:bg-status-error/10"
                          aria-label={`Remove ${f.filename}`}
                          onClick={() => void handleDeleteFileRow(f.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {voiceFeatureEnabled ? (
        <VoiceCaptureModal
          open={voiceModalOpen}
          onClose={cancelVoiceSession}
          transcript={lastVoiceTranscript}
          isRecording={isRecording}
          recordingTranscribing={recordingTranscribing}
          speechInterimActive={webSpeechSupported ? voiceSpeechInterim : undefined}
          onStop={handleVoiceModalStop}
          sending={sending}
          embedInPanel={embedInPanel}
        />
      ) : null}

      {clearConfirmOpen && (
        <div className={`${layer} inset-0 z-[60] flex items-center justify-center bg-black/60 p-4`}>
          <div className="w-full max-w-sm rounded-xl border border-subtle bg-surface p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-th-primary">Clear conversation?</h3>
            <p className="mt-2 text-sm text-th-secondary">This removes all messages for this agent. It cannot be undone.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="button-secondary" onClick={() => setClearConfirmOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl px-5 py-2.5 font-medium text-th-primary border border-muted bg-overlay transition-all duration-150 hover:bg-muted active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                disabled={clearingOrDeleting}
                onClick={() => void handleClearMessages()}
              >
                {clearingOrDeleting ? 'Clearing…' : 'Clear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div className={`${layer} inset-0 z-[60] flex items-center justify-center bg-black/60 p-4`}>
          <div className="w-full max-w-sm rounded-xl border border-subtle bg-surface p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-th-primary">Delete agent?</h3>
            <p className="mt-2 text-sm text-th-secondary">
              This permanently removes your subscription, chat history, and files for this agent.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="button-secondary" onClick={() => setDeleteConfirmOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl px-5 py-2.5 font-medium text-th-primary border border-muted bg-overlay transition-all duration-150 hover:bg-muted active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                disabled={clearingOrDeleting}
                onClick={() => void handleDeleteInstance()}
              >
                {clearingOrDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalContainer />
    </div>
  );
}
